import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'; 
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import capitalize from 'lodash/capitalize';
import { useDebouncedCallback } from 'use-debounce';
import { SlotInfo } from 'react-big-calendar';
import { usePrevious } from 'react-use';
import fastq from 'fastq';
import {
  actions, MeetingSlot, RootState, ThunkDispatchType, MeetingHoldEventErrorResolution, Meeting,
  InvalidParticipantVotes, MeetingFilter, MeetingUpdate, Grant, MeetingHoldEventError, MeetingStatus,
  RecurrenceRule,
} from '../../store';
import classes from './Schedule.module.css';
import { useMountEffect } from '../../utils/hooks';
import { PAGE_URL, PROVIDER, PROVIDER_BY_ID, PROVIDER_BY_NAME } from '../../constants';
import { 
  colorDisplayMeetingSlots, clearSelectedSlots, getInvalidParticipantVotes, getSlotCreateResult,
  getSlotEditResult, getSlotsDeleteResult, getMinSlotId, ExcludedSlotInfo, calendarToParticipant, getBookingLinkUrl,
  createMeetingSlotsFromRecurrenceRules, createRecurrenceRulesFromMeetingSlots,
} from '../../utils/scheduleUtils';
import { checkForCalendarGrants, getGrants } from '../../utils/authUtils';
import { DuplicateMeetingSubmitProps } from '../../components/Schedule/DuplicateMeetingModal/DuplicateMeetingModal';
import {
  selectCalendarLookupByCalendarId, selectLeaderLookup, selectUserCalendarEventsFromRTK,
  selectUserRecurringMeetingSlots
} from '../../store/schedule/selectors';
import { useLocation, useParams } from 'react-router';
import { clearNavigationState, router } from '../../router';
import { Clipboard } from '@capacitor/clipboard';
import Schedule from './Schedule';
import {
  currentMeetingSet, selectActiveCalendars, selectAllHiddenCalendarIds, selectCurrentMeeting,
  selectCurrentOrNewMeeting, selectDateRange, selectSelectedAdditionalCalendars, selectTimezone,
} from '../../store/scheduleUI';
import { scheduling } from '../../store/cabinetApi/scheduling';
import RecurrenceModal from '../../components/Schedule/RecurrenceModal';
import {
  getRecurringTrackerAlertMessage,
  getRecurringTrackerSeedSlots,
  RecurringTrackerMeetingSeed,
} from './recurringTrackerUtils';



type SlotState = {createdSlots: MeetingSlot[], deletedSlots: MeetingSlot[], updatedSlots: MeetingSlot[]};
type SlotOpTaskData = MeetingSlot|MeetingSlot[]|MeetingSlot['id']|MeetingSlot['id'][];
type SlotOpTask<T extends SlotOpTaskData> = { slot: T, operationFn: (slot: T) => Promise<void> };
type RecurrenceEditorState = {
  start: DateTime;
  end: DateTime;
  rule?: RecurrenceRule;
  editIndex?: number;
  slotId?: number;
};

const RECURRENCE_CONFLICT_CHECK_MONTHS = 1;

const slotOperationQueue = fastq.promise(
  <T extends SlotOpTaskData>(task: SlotOpTask<T>) => task.operationFn(task.slot),
  1,
);

const weekdayByLuxonNumber: Record<number, string> = {
  1: "MO",
  2: "TU",
  3: "WE",
  4: "TH",
  5: "FR",
  6: "SA",
  7: "SU",
};

const weekdayLabelByLuxonNumber: Record<number, string> = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

const getRuleParts = (rrule: string) => (
  Object.fromEntries(rrule.replace(/^RRULE:/i, "").split(";").map(part => {
    const [key, value] = part.split("=");
    return [key, value];
  }))
);

const serializeRuleParts = (parts: Record<string, string | undefined>) => (
  `RRULE:${Object.entries(parts).filter(([, value]) => !!value).map(([key, value]) => `${key}=${value}`).join(";")}`
);

const summarizeRecurrenceRule = (rule: RecurrenceRule) => {
  const parts = getRuleParts(rule.rrule);
  const start = DateTime.fromISO(rule.dtstart, { zone: rule.timezone || "UTC" }).setZone(rule.timezone || "UTC");
  const interval = Number(parts.INTERVAL || 1);
  const freq = parts.FREQ || "WEEKLY";
  const period = freq === "DAILY" ? "day" : freq === "MONTHLY" ? "month" : freq === "YEARLY" ? "year" : "week";
  const intervalText = `Every ${interval === 1 ? "" : `${interval} `}${interval === 1 ? period : `${period}s`}`;
  const periodQualifier = freq === "WEEKLY" ? ` on ${weekdayLabelByLuxonNumber[start.weekday]}`
    : freq === "MONTHLY" ? ` on day ${start.day}`
      : freq === "YEARLY" ? ` on ${start.toFormat("MMM d")}` : "";
  const endText = parts.COUNT ? `for ${parts.COUNT} occurrence${parts.COUNT === "1" ? "" : "s"}`
    : parts.UNTIL ? `until ${
      DateTime.fromFormat(parts.UNTIL.replace(/Z$/, ""), "yyyyMMdd'T'HHmmss", { zone: "UTC" })
        .setZone(rule.timezone || "UTC").toFormat("MMM d, yyyy")
    }` : "";
  return `${intervalText}${periodQualifier} ${endText}`.trim();
};

type RouteParams = { meetingId?: string;  provider?: string; };


const ScheduleContainer = (): ReactElement => {
  const user = useSelector((state: RootState) => state.auth.user);
  const auth = useSelector((state: RootState) => state.auth);
  const calendars = useSelector((state: RootState) => state.schedule.calendars);
  const associationsLoaded = useSelector((state: RootState) => state.schedule.associationsLoaded);
  const meetings = useSelector((state: RootState) => state.schedule.meetings);
  const meetingSlots = useSelector((state: RootState) => state.schedule.meetingSlots);
  const meetingsLoaded = useSelector((state: RootState) => state.schedule.meetingsLoaded);
  const calendarsLoaded = useSelector((state: RootState) => state.schedule.calendarsLoaded);
  const newMeeting = useSelector((state: RootState) => state.schedule.newMeeting);
  const scheduleErrors = useSelector((state: RootState) => state.schedule.scheduleErrors);
  const slotsLoaded = useSelector((state: RootState) => state.schedule.slotsLoaded);
  const calendarErrors = useSelector((state: RootState) => state.schedule.calendarErrors);
  const selectedLeaders = useSelector((state: RootState) => state.scheduleUI.selectedLeaderIds);
  const currentMeeting = useSelector(selectCurrentMeeting);
  const currentOrNewMeeting = useSelector(selectCurrentOrNewMeeting);
  const selectedAdditionalCalendars = useSelector(selectSelectedAdditionalCalendars);
  const activeCalendars = useSelector(selectActiveCalendars);
  const hiddenCalendarIds = useSelector(selectAllHiddenCalendarIds);
  const currentDateRange = useSelector(selectDateRange);
  const calendarTimezone = useSelector(selectTimezone);
  const leaderLookup = useSelector(selectLeaderLookup);
  const calendarLookup = useSelector(selectCalendarLookupByCalendarId);
  const leadersLoaded = useSelector((state: RootState) => state.leaders.loaded);
  
  const { data: schedulingPrefs } = scheduling.useAppSchedulingPreferencesListQuery();
  
  const userPrefs = schedulingPrefs?.user_prefs;

  const dispatch = useDispatch<ThunkDispatchType>();
  
  const fetchMeetingSlots = useCallback((unscheduled: boolean) => 
    dispatch(actions.schedule.fetchMeetingSlots(unscheduled)), [dispatch]);
  const fetchSlotsForMeeting = useCallback((meetingId: string) => 
    dispatch(actions.schedule.fetchSlotsForMeeting(meetingId)), [dispatch]);
  const startPollingMeetings = useCallback((query: Partial<MeetingFilter>) => 
    dispatch(actions.schedule.startPollingMeetings(query)), [dispatch]);
  const fetchMeeting = useCallback((meetingId: string) => 
    dispatch(actions.schedule.fetchMeeting(meetingId)), [dispatch]);
  const deleteNewMeeting = useCallback(() => dispatch(actions.schedule.deleteNewMeeting()), [dispatch]);
  const updateMeeting = useCallback((
    meeting: MeetingUpdate, timeSlots: MeetingSlot[], files?: File[] | undefined
  ) => dispatch(actions.schedule.updateMeeting(meeting, timeSlots, files)), [dispatch]);
  
  const updateTimeSlot = useCallback((timeSlot: MeetingSlot) =>
    dispatch(actions.schedule.updateTimeSlot(timeSlot)), [dispatch]);
  const deleteTimeSlot = useCallback((slot: MeetingSlot) => 
    dispatch(actions.schedule.deleteTimeSlot(slot)), [dispatch]);
  const batchDeleteTimeSlots = useCallback((slotIds: number[]) => 
    dispatch(actions.schedule.batchDeleteTimeSlots(slotIds)), [dispatch]);
  const batchCreateTimeSlots = useCallback((timeSlots: MeetingSlot[]) =>
    dispatch(actions.schedule.batchCreateTimeSlots(timeSlots)), [dispatch]);
  const handleMeetingSlotCalendarEventResolution = useCallback((args: MeetingHoldEventErrorResolution) =>
    dispatch(actions.schedule.handleMeetingSlotCalendarEventResolution(args)), [dispatch]);
  const logoutOAuth = useCallback((grant: Grant) => dispatch(actions.auth.logoutOAuth(grant)), [dispatch]);

  const setMeetingSlotCalendarEventError = useCallback((args: {
    meetingId: number;
    errors: MeetingHoldEventError[];
  }) => dispatch(actions.schedule.setMeetingSlotCalendarEventError(args)), [dispatch]);
  const duplicateMeeting = useCallback((meetingId: number, data: Partial<Meeting>) => 
    dispatch(actions.schedule.duplicateMeeting(meetingId, data)), [dispatch]);
  const deleteMeeting = useCallback((meeting: Meeting) => 
    dispatch(actions.schedule.deleteMeeting(meeting)), [dispatch]);
  const setNavbarExpanded = useCallback((expanded: boolean) => 
    dispatch(actions.cabUI.setCabNavbarExpanded(expanded)), [dispatch]);

  const params = useParams<RouteParams>();
  const location = useLocation();
  const navigate = router.navigate;

  const currentMeetingId = currentMeeting?.id;
  const meetingId = currentMeetingId || -1;

  const [selectedSlots, setSelectedSlots] = useState<MeetingSlot[]>(
    colorDisplayMeetingSlots(meetingSlots, [], meetings, currentMeetingId, classes.pattern, userPrefs)
  );
  const [/*loadingCalendars*/, setLoadingCalendars] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [pollUpdateOpen, setPollUpdateOpen] = useState(false);
  const [invalidatedParticipantVotes, setInvalidatedParticipantVotes] = useState<InvalidParticipantVotes>({});
  const [pendingSlotsToCreate, setPendingSlotsToCreate] = useState<MeetingSlot[]>([]);
  const [pendingSlotsToDelete, setPendingSlotsToDelete] = useState<MeetingSlot[]>([]);
  const [pendingSlotsToUpdate, setPendingSlotsToUpdate] = useState<MeetingSlot[]>([]);
  const [duplicateMeetingModalMeetingId, setDuplicateMeetingModalMeetingId] = useState<number>(-1);

  const [shareMeetingModalMeetingId, setShareMeetingModalMeetingId] = useState<number>(-1);
  const [updatingSlots, setUpdatingSlots] = useState(false);
  const [recurrenceEditor, setRecurrenceEditor] = useState<RecurrenceEditorState | null>(null);
  const [recurringTrackerAlertMessage, setRecurringTrackerAlertMessage] = useState<string | null>(null);

  const recurrenceConflictRange = useMemo(() => {
    if (!recurrenceEditor) return null;
    const startDate = recurrenceEditor.start.startOf("day");
    return {
      start: startDate,
      end: startDate.plus({ months: RECURRENCE_CONFLICT_CHECK_MONTHS }).endOf("day"),
    };
  }, [recurrenceEditor]);

  const visibleDisplayCalendars = useMemo(() => (
    activeCalendars.filter(c => !hiddenCalendarIds.includes(c.id)).map(c => c.calendar_id)
  ), [activeCalendars, hiddenCalendarIds]);

  const previousMeetingId = usePrevious(currentOrNewMeeting?.id);
  const previousAutoMergeSlots = usePrevious(currentOrNewMeeting?.auto_merge_slots);
  const previousDuration = usePrevious(currentOrNewMeeting?.duration_minutes);
  const previousAllDaySlots = usePrevious(currentOrNewMeeting?.all_day_slots);

  const userRecurringMeetingSlots = useSelector((s: RootState) => (
    selectUserRecurringMeetingSlots(
      s, selectedSlots,
      currentDateRange.start,
      currentDateRange.end,
      currentOrNewMeeting?.id,
      calendarTimezone?.name
    )
  ));

  const recurringSlots = useMemo(() => {
    const baseSlots = !userPrefs?.show_all_recurring_slots && !currentOrNewMeeting?.id ? [] : userRecurringMeetingSlots;
    
    // Filter recurring slots to only include meetings for selected leaders
    if (selectedLeaders.length > 0) {
      return baseSlots.filter(slot => {
        if (slot.meeting < 0) return true; // Include new meeting slots
        const meeting = meetings[slot.meeting];
        return meeting && meeting.leaders.some(leaderId => selectedLeaders.includes(leaderId));
      });
    }
    
    return baseSlots;
  }, [
    userPrefs?.show_all_recurring_slots, 
    currentOrNewMeeting?.id, 
    userRecurringMeetingSlots, 
    selectedLeaders, 
    meetings
  ]);

  const currentMeetingSlots = useMemo(
    () => selectedSlots.filter(slot => slot.meeting === meetingId || slot.meeting < 0),
    [selectedSlots, meetingId],
  );

  const recurringSeedSlots = useMemo(
    () => currentMeetingSlots.filter(slot => !slot.is_exclude && !!slot.recurrence_rule?.rrule)
      .sort((slotA, slotB) => slotA.start_date.localeCompare(slotB.start_date)),
    [currentMeetingSlots],
  );

  const recurrenceRules = useMemo(() => (
    createRecurrenceRulesFromMeetingSlots(recurringSeedSlots, calendarTimezone.name || "UTC")
  ), [calendarTimezone.name, recurringSeedSlots]);

  const recurringMeetingSlots = useMemo(() => {
    if (!currentOrNewMeeting?.is_recurring) return [];
    return createMeetingSlotsFromRecurrenceRules(
      recurrenceRules,
      currentDateRange.start,
      currentDateRange.end,
      currentOrNewMeeting.id,
      currentOrNewMeeting.title || "Recurring Meeting",
    );
  }, [
    currentDateRange.end, currentDateRange.start, currentOrNewMeeting?.id, currentOrNewMeeting?.is_recurring,
    currentOrNewMeeting?.title, recurrenceRules
  ]);

  const pendingRecurringMeetingSlots = useMemo(() => {
    const seedSlotsByMeeting = selectedSlots
      .filter(slot => (
        !!slot.recurrence_rule?.rrule
        && !slot.is_exclude
        && slot.meeting !== currentOrNewMeeting?.id
        && meetings[slot.meeting]?.status === MeetingStatus.PENDING
      ))
      .reduce<Record<number, MeetingSlot[]>>((slotsByMeeting, slot) => ({
        ...slotsByMeeting,
        [slot.meeting]: [...(slotsByMeeting[slot.meeting] || []), slot],
      }), {});

    return Object.entries(seedSlotsByMeeting).flatMap(([recurringMeetingId, seedSlots]) => {
      const meeting = meetings[Number(recurringMeetingId)];
      if (!meeting) return [];

      const rules = createRecurrenceRulesFromMeetingSlots(
        seedSlots,
        meeting.calendar_tz || calendarTimezone.name || "UTC"
      );

      return createMeetingSlotsFromRecurrenceRules(
        rules,
        currentDateRange.start,
        currentDateRange.end,
        meeting.id,
        meeting.title || "Recurring Meeting"
      ).map(({ recurrenceRuleIndex, ...slot }) => ({
        ...slot,
        editable: false,
        isRecurring: false,
      }));
    });
  }, [
    calendarTimezone.name, currentDateRange.end, currentDateRange.start, currentOrNewMeeting?.id, meetings,
    selectedSlots
  ]);

  const calendarSelectedSlots = useMemo(
    () => colorDisplayMeetingSlots(
      [
        ...selectedSlots.filter(slot => !slot.recurrence_rule?.rrule),
        ...pendingRecurringMeetingSlots,
        ...recurringMeetingSlots,
      ],
      selectedLeaders,
      meetings,
      currentMeetingId,
      classes.pattern,
      userPrefs
    ).map(slot => (
      slot.recurrenceRuleIndex != null
        ? {
          ...slot,
          editable: true,
          title: currentOrNewMeeting?.title || "Recurring Meeting",
        }
        : slot
    )),
    [
      currentMeetingId, currentOrNewMeeting?.title, meetings, pendingRecurringMeetingSlots, recurringMeetingSlots,
      selectedLeaders, selectedSlots, userPrefs
    ]
  );

  const hasGrant = !!user && checkForCalendarGrants(user.oauth_grant_details);

  const allLoaded = useMemo(() => (
    !hasGrant || (associationsLoaded && meetingsLoaded && calendarsLoaded)
  ), [hasGrant, associationsLoaded, meetingsLoaded, calendarsLoaded]);

  const currentMeetingErrors = useMemo(() => (
    currentMeeting?.id ? scheduleErrors[currentMeeting.id] || [] : []
  ), [currentMeeting?.id, scheduleErrors]);

  const openMeetingSettings = !!currentOrNewMeeting;

  const meetingToDuplicate: Meeting | undefined = useMemo(() => (
    meetings[duplicateMeetingModalMeetingId]
  ), [duplicateMeetingModalMeetingId, meetings]);



  // Create a stable dependency that only changes when relevant calendar properties change
  const activeCalendarDependencyParam = useMemo(() => (
    activeCalendars.toSorted((a, b) => a.id - b.id)
      .map(cal => `${cal.provider}:${cal.calendar_id}`).join(",")
  ), [activeCalendars]);

  // NOTE: activeCalendars will update after remote calendars are fetched with minor changes such as foregroundColor.
  // We do not want to trigger additional fetches due to these changes so we use a stable dependency for memoizing
  // the active google and ms calendars.
  const activeGoogleCalendars = useMemo(() => (
    (activeCalendars.filter((cal) => cal.provider === PROVIDER.GOOGLE.id).map((cal) => cal.calendar_id))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [activeCalendarDependencyParam]);

  const activeMSCalendars = useMemo(() => (
    (activeCalendars.filter((cal) => cal.provider === PROVIDER.MICROSOFT.id).map((cal) => cal.calendar_id))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [activeCalendarDependencyParam]);

  const {
    data: recurrenceConflictCalendarEvents,
    isLoading: recurrenceConflictCalendarEventsLoading,
  } = scheduling.useAppSchedulingCalendarEventsListQuery(
    {
      googleCalId: activeGoogleCalendars,
      msCalId: activeMSCalendars,
      startDate: recurrenceConflictRange?.start.toISO() || "",
      endDate: recurrenceConflictRange?.end.toISO() || "",
      forTimezone: calendarTimezone.name || "UTC",
    },
    {
      skip: !recurrenceConflictRange || (activeGoogleCalendars.length === 0 && activeMSCalendars.length === 0),
      selectFromResult: ({ data, ...other }) => ({
        ...other,
        data: data ? selectUserCalendarEventsFromRTK(
          data.data,
          new Set(visibleDisplayCalendars),
          leaderLookup,
          calendarLookup,
          recurrenceConflictRange?.start.toISO() || undefined,
          recurrenceConflictRange?.end.toISO() || undefined,
          !userPrefs?.show_canceled_declined_events
        ) : undefined
      })
    }
  );

  // This debounce is because we are updating slots on a few criteria, some of which change simultaneously.
  //   E.g. If Meeting X with Exec A is selected, switching to Meeting Y with Exex B would trigger this
  //   update sequence twice consecutively since both the meeting and exec are changing.
  const updateSelectedSlotsDebounced = useDebouncedCallback(() => {
    if (newMeeting) {
      setSelectedSlots(colorDisplayMeetingSlots([...meetingSlots, ...selectedSlots.filter(s => s.id < 0)],
        selectedLeaders, meetings, currentMeetingId, classes.pattern, userPrefs));
    } else {
      setSelectedSlots(colorDisplayMeetingSlots(meetingSlots,
        selectedLeaders, meetings, currentMeetingId, classes.pattern, userPrefs));
    }
  }, 100);

  const handleNewMeetingClick = useCallback((
    poll = false, reusable = false, bookOnCreate = false, recurring = false
  ) => {
    const newMtg = dispatch(actions.schedule.createNewMeeting(
      selectedLeaders,
      selectedAdditionalCalendars.map((cal, idx) => calendarToParticipant(-1, cal, (idx + 1) * -1)),
      userPrefs,
      poll,
      reusable,
      bookOnCreate,
      recurring,
    ));

    return newMtg;
  }, [selectedAdditionalCalendars, dispatch, selectedLeaders, userPrefs]);


  const recalculateSelectedSlots = () => {
    if (currentMeetingSlots.length > 0 && currentOrNewMeeting) {
      let newSlotInfo = getSlotsDeleteResult(currentMeetingSlots);
      
      currentMeetingSlots.forEach(slot => {
        const slotInfo: SlotInfo & { id: number } = {
          id: slot.id,
          start: DateTime.fromISO(slot.start_date).toJSDate(),
          end: DateTime.fromISO(slot.end_date).toJSDate(),
          slots: [],
          action: 'select',
        };
        
        // need to pass in the previously created slots during the function when we call this
        const slotCreateResult = getSlotCreateResult(
          slotInfo, slotInfo.id, currentOrNewMeeting, newSlotInfo.createdSlots, slot.is_exclude
        );

        newSlotInfo = [newSlotInfo, slotCreateResult].reduce((a, b) => ({
          createdSlots: [...a.createdSlots, ...b.createdSlots],
          deletedSlots: [...a.deletedSlots, ...b.deletedSlots],
          updatedSlots: [...a.updatedSlots, ...b.updatedSlots],
        }));

      });

      attemptSlotUpdates(newSlotInfo);
    }
  };

  const updateSlotNames = useCallback((newMeetingName: string) => {
    setSelectedSlots(selectedSlots.map(slot => ({
      ...slot,
      title: slot.meeting === currentMeetingId ? newMeetingName : slot.title,
    })));
  // NOTE: just for selectedSlots
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMeetingId, JSON.stringify(selectedSlots)]);

  const updateSlots = useCallback(async ({createdSlots, updatedSlots, deletedSlots}: SlotState) => {
    setUpdatingSlots(true);

    const slotsToDelete = deletedSlots.filter(slot => slot.id > 0);
    if (slotsToDelete.length === 1) {
      slotOperationQueue.push({ slot: slotsToDelete[0], operationFn: deleteTimeSlot });
    } else if (slotsToDelete.length > 1) {
      slotOperationQueue.push({ slot: slotsToDelete.map(slot => slot.id), operationFn: batchDeleteTimeSlots });
    }

    const slotsToUpdate = updatedSlots.filter(slot => slot.id > 0);
    slotsToUpdate.forEach(slot => {
      slotOperationQueue.push({ slot, operationFn: updateTimeSlot });
    });

    if (meetingId > 0 && createdSlots.length > 0) {
      slotOperationQueue.push({ slot: createdSlots, operationFn: batchCreateTimeSlots });
    }

    const nextSlots = selectedSlots
      .map(existingEvent => updatedSlots.find(updateSlot => updateSlot.id === existingEvent.id) || existingEvent)
      .filter(existingEvent => !deletedSlots.find(removeSlot => removeSlot.id === existingEvent.id))
      .concat(createdSlots);
    const coloredDisplayMeetingSlots = colorDisplayMeetingSlots(
      nextSlots, selectedLeaders, meetings, currentMeetingId, classes.pattern, userPrefs
    );
    setSelectedSlots(coloredDisplayMeetingSlots);

    // TODO: there may still be an issue where this hangs... seems to happen when multiple queue pushes
    // happen above
    await slotOperationQueue.drained();

    setUpdatingSlots(false);
  }, [batchCreateTimeSlots, batchDeleteTimeSlots, currentMeetingId,
    deleteTimeSlot, meetingId, meetings, userPrefs, selectedLeaders, selectedSlots, updateTimeSlot]);

  const validateSlotChanges = useCallback(({
    paramMeetingId, createdSlots, updatedSlots, deletedSlots, existingSlots
  }: {
    paramMeetingId: number, createdSlots: MeetingSlot[], updatedSlots: MeetingSlot[],
    deletedSlots: MeetingSlot[], existingSlots: MeetingSlot[]
  }) => {
    const meetingParam = meetings[paramMeetingId];

    if (meetingParam && meetingParam.is_poll) {
      const invalidVotesFound = getInvalidParticipantVotes(
        meetingParam, createdSlots, deletedSlots, updatedSlots, existingSlots
      );
      
      if (Object.keys(invalidVotesFound).length > 0) {
        setInvalidatedParticipantVotes(invalidVotesFound);
        setPendingSlotsToCreate(createdSlots);
        setPendingSlotsToDelete(deletedSlots);
        setPendingSlotsToUpdate(updatedSlots);
        setPollUpdateOpen(true);
      } else {
        return updateSlots({ createdSlots, deletedSlots, updatedSlots });
      }
    } else {
      return updateSlots({ createdSlots, deletedSlots, updatedSlots });
    }
    return Promise.resolve();
  }, [meetings, updateSlots]);

  const attemptSlotUpdates = useCallback((slotState: SlotState) => {
    return validateSlotChanges({
      ...slotState,
      existingSlots: selectedSlots,
      paramMeetingId: meetingId,
    });
  }, [meetingId, selectedSlots, validateSlotChanges]);

  const handleOpenMeeting = useCallback((handleOpenMeetingId: number): void => {
    setRecurringTrackerAlertMessage(null);
    navigate(`${PAGE_URL.SCHEDULE}/${handleOpenMeetingId}/`);
  }, [navigate]);

  const handleCancel = useCallback(() => {
    setRecurringTrackerAlertMessage(null);
    if (currentMeetingId) {
      navigate(PAGE_URL.SCHEDULE);
    } else {
      deleteNewMeeting();
    }
    setSelectedSlots(clearSelectedSlots(selectedSlots));
    clearNavigationState();
  }, [deleteNewMeeting, selectedSlots, currentMeetingId, navigate]);

  const handleResolution = useCallback((context: MeetingHoldEventErrorResolution) => {
    handleMeetingSlotCalendarEventResolution(context).then(res => {
      if (res) {
        if (meetingId > 0) {
          const schedErrors = scheduleErrors[meetingId].filter(
            error => error.meetingSlotCalendarEventId !== context.meetingSlotCalendarEventId
          );
          setMeetingSlotCalendarEventError(
            {
              errors: schedErrors,
              meetingId: meetingId
            }
          );
          if (schedErrors.length === 0) {
            setErrorModalOpen(false);
          }
        }
      }
    });
  }, [meetingId, scheduleErrors, setErrorModalOpen, handleMeetingSlotCalendarEventResolution,
    setMeetingSlotCalendarEventError]);

  const handleDuplicateMeeting = useCallback((handleDuplicateMeetingId: number) => {
    setDuplicateMeetingModalMeetingId(handleDuplicateMeetingId);
  }, []);

  const handleSubmitDuplicateMeeting = useCallback(async (data: DuplicateMeetingSubmitProps) => {
    const meeting = await duplicateMeeting(duplicateMeetingModalMeetingId, data);
    if (meeting) {
      navigate(`${PAGE_URL.SCHEDULE}/${meeting.id}/`);
      setDuplicateMeetingModalMeetingId(-1);
      setShareMeetingModalMeetingId(meeting.id);
      return meeting;
    }
    return undefined;
  }, [duplicateMeetingModalMeetingId, navigate, setDuplicateMeetingModalMeetingId, setShareMeetingModalMeetingId,
    duplicateMeeting]);

  const handleDeleteMeeting = useCallback(async (handleDeleteMeetingId: number) => {
    const meeting = meetings[handleDeleteMeetingId];
    if (!meeting) return;
    
    await deleteMeeting(meeting);
    if (meeting.id === currentMeetingId) {
      navigate(PAGE_URL.SCHEDULE);
    }
  }, [meetings, deleteMeeting, currentMeetingId, navigate]);

  const handleShareMeeting = useCallback((mId: number) => {
    setShareMeetingModalMeetingId(mId);
  }, []);

  const handleCopyLink = useCallback(async (copyLinkMeetingId: number) => {
    const mtg = meetings[copyLinkMeetingId];
    if (mtg) {
      await Clipboard.write({ string: getBookingLinkUrl(mtg) });
      dispatch(actions.globalMessage.sendMessage({
        timeout: 750, message: 'Copied', header: "",
        position: { vertical: 'top', horizontal: 'center' },
        active: true, severity: 'success', autoDismiss: true
      }));
    }
  }, [dispatch, meetings]);

  const handleShareCurrentMeeting = useCallback(() => {
    handleShareMeeting(currentMeetingId || -1);
  }, [handleShareMeeting, currentMeetingId]);

  const handlePollUpdateAccept = useCallback(() => {
    updateSlots({
      createdSlots: pendingSlotsToCreate,
      deletedSlots: pendingSlotsToDelete,
      updatedSlots: pendingSlotsToUpdate
    });
    setInvalidatedParticipantVotes({});
    setPollUpdateOpen(false);
  }, [pendingSlotsToCreate, pendingSlotsToDelete, pendingSlotsToUpdate, updateSlots]);

  const handlePollUpdateReject = useCallback(() => {
    setPendingSlotsToCreate([]);
    setPendingSlotsToDelete([]);
    setPendingSlotsToUpdate([]);
    setInvalidatedParticipantVotes({});
    setPollUpdateOpen(false);
  }, []);

  const handleCreateSlot = useCallback((info: SlotInfo) => {
    if (!leadersLoaded) return;
    
    // make sure we handle first slot selected when meeting panel is closed
    const mtg = currentOrNewMeeting || handleNewMeetingClick();
    if (mtg?.is_recurring) {
      const timezone = calendarTimezone.name || "UTC";
      setRecurrenceEditor({
        start: DateTime.fromJSDate(info.start).setZone(timezone),
        end: DateTime.fromJSDate(info.end).setZone(timezone),
      });
      return;
    }
    // if an id is given, use it (useful for batch/re-creation). Otherwise generate one.
    const newSlotId = getMinSlotId(selectedSlots) - 1;
    
    // Allow multiple slots for all meeting types, including calendar events
    attemptSlotUpdates(getSlotCreateResult(info, newSlotId, mtg, currentMeetingSlots, false));
  }, [attemptSlotUpdates, calendarTimezone.name, currentMeetingSlots, currentOrNewMeeting, handleNewMeetingClick,
    selectedSlots, leadersLoaded]);

  const handleConfirmRecurrence = useCallback(async (rule: RecurrenceRule) => {
    if (!currentOrNewMeeting?.is_recurring) return;
    const timezone = rule.timezone || calendarTimezone.name || "UTC";
    const start = DateTime.fromISO(rule.dtstart, { zone: timezone }).setZone(timezone);
    if (!start.isValid) return;
    const recurrenceSlot: MeetingSlot = {
      ...(recurrenceEditor?.slotId != null
        ? currentMeetingSlots.find(slot => slot.id === recurrenceEditor.slotId) || {}
        : {}),
      id: recurrenceEditor?.slotId ?? getMinSlotId(selectedSlots) - 1,
      meeting: currentOrNewMeeting.id,
      start_date: start.toISO() || rule.dtstart,
      end_date: start.plus({ minutes: rule.duration_minutes }).toISO() || rule.dtstart,
      is_exclude: false,
      all_day: false,
      recurrence_rule: {
        rrule: rule.rrule,
      },
      title: currentOrNewMeeting.title,
    };
    await attemptSlotUpdates({
      createdSlots: recurrenceEditor?.slotId == null ? [recurrenceSlot] : [],
      updatedSlots: recurrenceEditor?.slotId != null ? [recurrenceSlot] : [],
      deletedSlots: [],
    });
    setRecurrenceEditor(null);
  }, [
    attemptSlotUpdates, calendarTimezone.name, currentMeetingSlots, currentOrNewMeeting, recurrenceEditor?.slotId,
    selectedSlots
  ]);

  const handleEditRecurrence = useCallback((rule: RecurrenceRule, index: number) => {
    const timezone = rule.timezone || calendarTimezone.name || "UTC";
    const start = DateTime.fromISO(rule.dtstart, { zone: timezone }).setZone(timezone);
    const slot = recurringSeedSlots[index];
    if (!start.isValid) return;
    setRecurrenceEditor({
      start,
      end: start.plus({ minutes: rule.duration_minutes }),
      rule,
      editIndex: index,
      slotId: slot?.id,
    });
  }, [calendarTimezone.name, recurringSeedSlots]);

  const handleOpenRecurrenceIndex = useCallback((index: number) => {
    const rule = recurrenceRules[index];
    if (!rule) return;
    handleEditRecurrence(rule, index);
  }, [handleEditRecurrence, recurrenceRules]);

  const handleDeleteRecurrence = useCallback(async (index: number) => {
    if (!currentOrNewMeeting?.is_recurring) return;
    const slot = recurringSeedSlots[index];
    if (!slot) return;
    await attemptSlotUpdates({
      createdSlots: [],
      updatedSlots: [],
      deletedSlots: [slot],
    });
  }, [attemptSlotUpdates, currentOrNewMeeting?.is_recurring, recurringSeedSlots]);

  const handleEditRecurrenceTime = useCallback(async (index: number, start: Date, end: Date) => {
    const rule = recurrenceRules[index];
    const slot = recurringSeedSlots[index];
    if (!rule) return;
    const timezone = rule.timezone || calendarTimezone.name || "UTC";
    const zonedStart = DateTime.fromJSDate(start).setZone(timezone);
    const zonedEnd = DateTime.fromJSDate(end).setZone(timezone);
    if (!zonedStart.isValid || !zonedEnd.isValid) return;

    const parts = getRuleParts(rule.rrule);
    if (parts.FREQ === "WEEKLY") {
      parts.BYDAY = weekdayByLuxonNumber[zonedStart.weekday];
    } else if (parts.FREQ === "MONTHLY") {
      parts.BYMONTHDAY = zonedStart.day.toString();
    } else if (parts.FREQ === "YEARLY") {
      parts.BYMONTH = zonedStart.month.toString();
      parts.BYMONTHDAY = zonedStart.day.toString();
    }

    const nextRule = {
      ...rule,
      dtstart: zonedStart.toISO() || rule.dtstart,
      duration_minutes: Math.max(1, Math.round(zonedEnd.diff(zonedStart, "minutes").minutes)),
      rrule: serializeRuleParts(parts),
    };

    if (slot) {
      await attemptSlotUpdates({
        createdSlots: [],
        updatedSlots: [{
          ...slot,
          start_date: nextRule.dtstart,
          end_date: zonedStart.plus({ minutes: nextRule.duration_minutes }).toISO() || slot.end_date,
          recurrence_rule: {
            rrule: serializeRuleParts(parts),
          },
        }],
        deletedSlots: [],
      });
    } else {
      handleEditRecurrence({
        ...nextRule,
        summary: summarizeRecurrenceRule(nextRule),
      }, index);
    }
  }, [
    attemptSlotUpdates, calendarTimezone.name, handleEditRecurrence, recurrenceRules, recurringSeedSlots
  ]);

  const handleCreateExcludeSlots = useCallback((slots: ExcludedSlotInfo[]) => {
    let newSlotId = getMinSlotId(selectedSlots) - 1;
    const mtg = currentOrNewMeeting;
    if (!mtg) return;
    // TODO: This is not an optimal strategy, we should not need to create a new 
    // variable to store slotState, but selectedSlot state is not updating between cycles.
    let slotState: SlotState = {createdSlots: [], deletedSlots: [], updatedSlots: []};
    slots.forEach(slot => {
      const slotCreateResult = getSlotCreateResult(slot, newSlotId, mtg, currentMeetingSlots, true);
      slotState = {
        createdSlots:[...slotState.createdSlots, ...slotCreateResult.createdSlots], 
        deletedSlots: [...slotState.deletedSlots, ...slotCreateResult.deletedSlots], 
        updatedSlots:[...slotState.updatedSlots, ...slotCreateResult.updatedSlots]
      };
      newSlotId--;
    });
    attemptSlotUpdates(slotState);
  }, [attemptSlotUpdates, currentMeetingSlots, currentOrNewMeeting, selectedSlots]);

  const handleEditSlot = useCallback((eventId: string, start: Date, end: Date, isExcluded: boolean) => {
    attemptSlotUpdates(getSlotEditResult(
      eventId, start, end, currentMeetingSlots, isExcluded, currentOrNewMeeting || undefined
    ));
  }, [attemptSlotUpdates, currentMeetingSlots, currentOrNewMeeting]);

  const handleDeleteSlots = useCallback((slots: MeetingSlot[]) => {
    attemptSlotUpdates(getSlotsDeleteResult(slots));
  }, [attemptSlotUpdates]);

  const handleConvertToOneOff = useCallback(async (handleConvertToOneOffMeetingId: number) => {
    await updateMeeting({ id: handleConvertToOneOffMeetingId, use_template_parent: false }, []);
  }, [updateMeeting]);

  // set currentMeetingId from url params and fetch if necessary
  useEffect(() => {
    const cmId = params.meetingId ? Number(params.meetingId) : null;
    if (cmId && cmId > 0) {
      setRecurringTrackerAlertMessage(null);
    }
    if (cmId != currentMeetingId) {
      dispatch(currentMeetingSet(cmId));

      // make sure any valid meeting given in the url is loaded, fetch slots if meeting is scheduled
      if (cmId && !meetings[cmId]) {
        fetchMeeting(cmId.toString());
        fetchSlotsForMeeting(cmId.toString());
      } else if (cmId && meetings[cmId]?.status !== MeetingStatus.PENDING) {
        fetchSlotsForMeeting(cmId.toString());
      }
    }
  }, [params.meetingId, meetings, dispatch, currentMeetingId, fetchMeeting, fetchSlotsForMeeting]);

  // start/stop polling meetings on mount/unmount
  useMountEffect(() => {
    let stop = () => {
      return;
    };
    (async () => {
      stop = await startPollingMeetings({status: MeetingStatus.PENDING});
    })();

    return () => stop();
  });


  // RTK Query automatically refetches when parameters change, so no manual refetch needed

  // other mount/unmount effects
  useMountEffect(() => {
    setNavbarExpanded(false);
    dispatch(actions.schedule.fetchZoomSettings());

    // If no meeting slots are loaded yet, load only unscheduled
    if (!slotsLoaded) {
      fetchMeetingSlots(true);
    }

    return () => {
      deleteNewMeeting();
      dispatch(currentMeetingSet(null));
    };
  });

  // fetch calendars and/or remote calendars if user grant changes
  useEffect(() => {
    if (user?.oauth_grant_details) {
      dispatch(actions.schedule.fetchCalendars());

      const hasGrantCheck = checkForCalendarGrants(user.oauth_grant_details);
      if (hasGrantCheck) {
        setLoadingCalendars(true);
        dispatch(actions.schedule.fetchRemoteCalendars())
          .then(() => setLoadingCalendars(false));
      }
    }
  }, [dispatch, user?.oauth_grant_details]);

  // handle initially opening correct meeting type based on navigation state parameter
  useEffect(() => {
    if (location.state?.initSchedule) {
      setRecurringTrackerAlertMessage(null);
      deleteNewMeeting();
      updateSelectedSlotsDebounced();
    } else if (location.state?.createMeeting) {
      setRecurringTrackerAlertMessage(null);
      handleNewMeetingClick();
      updateSelectedSlotsDebounced();
    } else if (location.state?.createMeetingPoll) {
      setRecurringTrackerAlertMessage(null);
      handleNewMeetingClick(true);
      updateSelectedSlotsDebounced();
    } else if (location.state?.createReusableMeeting) {
      setRecurringTrackerAlertMessage(null);
      handleNewMeetingClick(false, true);
      updateSelectedSlotsDebounced();
    } else if (location.state?.createRecurringMeeting) {
      const newMtg = handleNewMeetingClick(false, false, false, true);
      const seed = location.state?.recurringTrackerSeed as RecurringTrackerMeetingSeed | undefined;
      if (seed) {
        const draftMeetingId = -1;
        const title = seed.title || newMtg.title;
        setRecurringTrackerAlertMessage(getRecurringTrackerAlertMessage(seed));
        dispatch(actions.schedule.updateNewMeeting({
          ...newMtg,
          title,
          duration_minutes: seed.durationMinutes || newMtg.duration_minutes,
        }));

        const seedSlots = getRecurringTrackerSeedSlots(
          seed,
          draftMeetingId,
          title || "Recurring Meeting",
          calendarTimezone.name || "UTC"
        );
        if (seedSlots.length) {
          setSelectedSlots(prevSlots => colorDisplayMeetingSlots(
            [...prevSlots.filter(slot => slot.meeting !== draftMeetingId), ...seedSlots],
            selectedLeaders,
            meetings,
            draftMeetingId,
            classes.pattern,
            userPrefs
          ));
        }
      } else {
        setRecurringTrackerAlertMessage(null);
        updateSelectedSlotsDebounced();
      }
    } else if (location.state?.createCalendarEvent) {
      setRecurringTrackerAlertMessage(null);
      handleNewMeetingClick(false, false, true);
      updateSelectedSlotsDebounced();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.state?.initSchedule, location.state?.createMeeting,
    location.state?.createMeetingPoll, location.state?.createReusableMeeting, location.state?.createRecurringMeeting,
    location.state?.createCalendarEvent, location.state?.recurringTrackerSeed]);


  // logout OAuth provider if we get a tokens_invalid error
  useEffect(() => {
    if (calendarErrors.some(error => error.name === 'tokens_invalid')) {
      const grants = getGrants(auth);
      const providerNames = calendarErrors.map(error => {
        // NOTE: This will log out all usernames for the provider. This will NOT work well when we allow
        //   multiple accounts for each provider to be authenticated simultanously. I.e. one error for one would
        //   log them ALL out.
        grants.forEach(grant => {
          const grantProvider = PROVIDER_BY_NAME[grant.provider];
          if (grantProvider.id === error.provider) {
            logoutOAuth(grant);
          }
        });
        return capitalize(PROVIDER_BY_ID[error.provider].name);
      }).join(', ');
      // TODO: would be good to have a global alert popup we can trigger so this is visible after navigation
      // setAccountManagementMessage('Please re-authenticate with ' + providerNames);
      console.log('Please re-authenticate with ' + providerNames);
      navigate(PAGE_URL.INTEGRATION_SETTINGS);
    } else {
      // setAccountManagementMessage('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarErrors, logoutOAuth, navigate]);

  // update slots
  useEffect(() => {
    if (!updatingSlots && allLoaded) {
      // Combining meetingSlots and selectedSlots is required to prevent losing slots from unsaved meetings      
      updateSelectedSlotsDebounced();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMeetingId, Object.values(meetings).map(m => `${m.id}:${m.status}`).join(','),
    meetingSlots, updatingSlots, allLoaded, selectedLeaders, newMeeting]);

  useEffect(() => {
    if (associationsLoaded && calendars.length === 0) {
      navigate(PAGE_URL.MANAGE_CALENDARS);
    }
  }, [associationsLoaded, calendars.length, navigate]);

  useEffect(() => {
    if (!currentOrNewMeeting?.id || !previousMeetingId || previousMeetingId !== currentOrNewMeeting.id) return;
    if (previousAutoMergeSlots != null && currentOrNewMeeting?.auto_merge_slots != null
      && previousAutoMergeSlots !== currentOrNewMeeting?.auto_merge_slots && !currentOrNewMeeting.auto_merge_slots
    ) {
      recalculateSelectedSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrNewMeeting?.auto_merge_slots, previousAutoMergeSlots, currentOrNewMeeting?.id, previousMeetingId]);

  useEffect(() => {
    if (!currentOrNewMeeting?.id || !previousMeetingId || previousMeetingId !== currentOrNewMeeting.id) return;
    if ((currentOrNewMeeting?.duration_minutes || 0) > 0 && previousDuration !== currentOrNewMeeting.duration_minutes
      && currentOrNewMeeting?.auto_merge_slots === false
    ) {
      recalculateSelectedSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrNewMeeting?.duration_minutes, currentOrNewMeeting?.id, previousMeetingId]);

  useEffect(() => {
    if (!currentOrNewMeeting?.id || !previousMeetingId || previousMeetingId !== currentOrNewMeeting.id) return;
    if (!previousAllDaySlots && !!currentOrNewMeeting?.all_day_slots) {
      recalculateSelectedSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrNewMeeting?.all_day_slots, currentOrNewMeeting?.id, previousMeetingId, previousAllDaySlots]);

  return (
    <>
      <Schedule
        currentMeeting={currentMeeting}
        currentMeetingErrors={currentMeetingErrors}
        errorModalOpen={errorModalOpen}
        handleResolution={handleResolution}
        calendars={calendars}
        handleCancel={handleCancel}
        handleOpenMeeting={handleOpenMeeting}
        allLoaded={allLoaded}
        openMeetingSettings={openMeetingSettings}
        selectedSlots={calendarSelectedSlots}
        recurringSlots={recurringSlots}
        handleCreateSlot={handleCreateSlot}
        handleEditSlot={handleEditSlot}
        handleDeleteSlots={handleDeleteSlots}
        handleDuplicateMeeting={handleDuplicateMeeting}
        handleDeleteMeeting={handleDeleteMeeting}
        handleShareMeeting={handleShareMeeting}
        handleCopyLink={handleCopyLink}
        handleConvertToOneOff={handleConvertToOneOff}
        currentMeetingSlots={currentMeetingSlots}
        updateSlotNames={updateSlotNames}
        setErrorModalOpen={setErrorModalOpen}
        handleShareCurrentMeeting={handleShareCurrentMeeting}
        updatingSlots={updatingSlots}
        pollUpdateOpen={pollUpdateOpen}
        handlePollUpdateAccept={handlePollUpdateAccept}
        handlePollUpdateReject={handlePollUpdateReject}
        invalidatedParticipantVotes={invalidatedParticipantVotes}
        handleCreateExcludeSlots={handleCreateExcludeSlots}
        handleEditRecurrence={handleEditRecurrence}
        handleDeleteRecurrence={handleDeleteRecurrence}
        handleOpenRecurrenceIndex={handleOpenRecurrenceIndex}
        handleEditRecurrenceTime={handleEditRecurrenceTime}
        shareMeetingModalMeetingId={shareMeetingModalMeetingId}
        setShareMeetingModalMeetingId={setShareMeetingModalMeetingId}
        setDuplicateMeetingModalMeetingId={setDuplicateMeetingModalMeetingId}
        handleSubmitDuplicateMeeting={handleSubmitDuplicateMeeting}
        meetingToDuplicate={meetingToDuplicate}
        selectedLeaders={selectedLeaders}
        googleCalIds={activeGoogleCalendars}
        msCalIds={activeMSCalendars}
        forTimezone={calendarTimezone.name || "UTC"}
        showCanceledDeclined={!userPrefs?.show_canceled_declined_events}
        prefetchNextPeriod={true}
        onError={() => {}}
        headerAlertMessage={recurringTrackerAlertMessage}
      />
      {recurrenceEditor && (
        <RecurrenceModal
          open={!!recurrenceEditor}
          start={recurrenceEditor.start}
          end={recurrenceEditor.end}
          timezone={recurrenceEditor.rule?.timezone || calendarTimezone.name || "UTC"}
          calendarEvents={recurrenceConflictCalendarEvents || []}
          calendarEventsLoading={recurrenceConflictCalendarEventsLoading}
          conflictCheckEndDate={recurrenceConflictRange?.end}
          initialRule={recurrenceEditor.rule}
          onClose={() => setRecurrenceEditor(null)}
          onConfirm={handleConfirmRecurrence}
          onRemove={recurrenceEditor.editIndex == null ? undefined : async () => {
            await handleDeleteRecurrence(recurrenceEditor.editIndex as number);
            setRecurrenceEditor(null);
          }}
        />
      )}
    </>
  );
};

export default ScheduleContainer;
